import { faX } from "@fortawesome/free-solid-svg-icons"
import { InputAdornment, Modal, Typography } from "@mui/material"
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import borders from "assets/theme/base/borders"
import colors from "assets/theme/base/colors"
import MDBox from "components/atoms/MDBox/MDBox"
import MDButton from "components/atoms/MDButton/MDButton"
import MDInput from "components/atoms/MDInput/MDInput"
import Spinner from "components/molecules/Spinner/Spinner"
import { containerStyles, snakeToTitleCase, validateDateFormat } from "helpers/helpers"
import { useAppointmentData } from "hooks/useAppointmentData"
import { useAppointmentMutations } from "hooks/useAppointmentMutations"
import { useAuth } from "hooks/useAuth"
import { useBreakpoints } from "hooks/useBreakpoints"
import useForm from "hooks/useForm"
import { useOrgData } from "hooks/useOrgData"
import { usePatientMutations } from "hooks/usePatientMutations"
import { usePatientData } from "hooks/usePatientData"
import { Appointment } from "models/AppointmentModels"
import { Patient } from "models/PatientModels"
import { Breakpoints, MUIColors, Shadows } from "models/StyleModels"
import { FullJoints } from "models/XrayModels"
import SearchIcon from '@mui/icons-material/Search';
import Switch from "components/atoms/Switch/Switch";
import { useState, useEffect } from "react"
import DropdownSelect from "components/molecules/DropdownSelect/DropdownSelect"

import {
    ModalTitle,
    ErrorText,
    CloseIcon,
    SearchContainer,
    PatientSearchDropdown,
    PatientSearchDropdownItem,
    FormContainer,
    JointSelectionContainer,
    JointSelectionBox,
    JointSelectionTitle,
    JointOptionsContainer,
    JointLabel,
    JointCheckbox,
    JointText,
    PriorityContainer,
    PriorityTitle,
    PrioritySwitchContainer,
    PriorityDropdownContainer,
    ModalContainer,
    DatePickerWrapper
} from "./AddPatientModal.styles"

export enum FormKeys {
    PATIENT_FIRST_NAME = 'PATIENT_FIRST_NAME',
    PATIENT_LAST_NAME = 'PATIENT_LAST_NAME',
    PATIENT_ID_NUMBER = 'PATIENT_ID_NUMBER',
    PATIENT_DOB = 'PATIENT_DOB',
    PROVIDER = 'PROVIDER',
    RIGHT_KNEE = 'RIGHT_KNEE',
    LEFT_KNEE = 'LEFT_KNEE',
    RIGHT_HIP = 'RIGHT_HIP',
    LEFT_HIP = 'LEFT_HIP',
    APPT_DATE = 'APPT_DATE',
    PRIORITY_1 = 'PRIORITY_1',
    PRIORITY_2 = 'PRIORITY_2',
    PRIORITY_3 = 'PRIORITY_3',
    PRIORITY_4 = 'PRIORITY_4'
}

export type FormValues = {
    [FormKeys.PATIENT_FIRST_NAME]: string;
    [FormKeys.PATIENT_LAST_NAME]: string;
    [FormKeys.PATIENT_ID_NUMBER]: string;
    [FormKeys.PATIENT_DOB]: string;
    [FormKeys.PROVIDER]: string;
    [FormKeys.RIGHT_KNEE]: boolean;
    [FormKeys.LEFT_KNEE]: boolean;
    [FormKeys.RIGHT_HIP]: boolean;
    [FormKeys.LEFT_HIP]: boolean;
    [FormKeys.APPT_DATE]: string;
    [FormKeys.PRIORITY_1]: string;
    [FormKeys.PRIORITY_2]: string;
    [FormKeys.PRIORITY_3]: string;
    [FormKeys.PRIORITY_4]: string;
}

interface AddPatientModalContentProps {
    closeModal(): void;
    onSuccess(): void;
    isEdit?: boolean;
    patient?: Patient | null;
    appointment?: Appointment | null;
    refetchTriageAppointmentStatuses?: () => Promise<any>;
}

const Content: React.FC<AddPatientModalContentProps> = ({ closeModal, onSuccess, isEdit, patient, appointment, refetchTriageAppointmentStatuses }) => {
    const { user } = useAuth();

    const {
        org,
    } = useOrgData({ preventRefetch: true });

    const {
        createPatient,
        createPatientError,
        createPatientLoading,
        updatePatient,
        updatePatientError,
        updatePatientLoading,
    } = usePatientMutations();

    const {
        createAppointment,
        updateAppointment,
        createAppointmentError,
        createAppointmentLoading,
        updateAppointmentError,
        updateAppointmentLoading,
    } = useAppointmentMutations();

    const {
        values: formValues,
        handleChange,
        setFormValue,
        setFormValues,
    } = useForm<FormValues>({
        [FormKeys.PATIENT_FIRST_NAME]: patient?.first_name || '',
        [FormKeys.PATIENT_LAST_NAME]: patient?.last_name || '',
        [FormKeys.PATIENT_ID_NUMBER]: patient?.external_id || '',
        [FormKeys.PATIENT_DOB]: patient?.date_of_birth || '',
        [FormKeys.PROVIDER]: patient?.provider || '',
        [FormKeys.RIGHT_KNEE]: isEdit && appointment?.joints ? appointment.joints[FullJoints.RIGHT_KNEE] === 1 : false,
        [FormKeys.LEFT_KNEE]: isEdit && appointment?.joints ? appointment.joints[FullJoints.LEFT_KNEE] === 1 : false,
        [FormKeys.RIGHT_HIP]: isEdit && appointment?.joints ? appointment.joints[FullJoints.RIGHT_HIP] === 1 : false,
        [FormKeys.LEFT_HIP]: isEdit && appointment?.joints ? appointment.joints[FullJoints.LEFT_HIP] === 1 : false,
        [FormKeys.APPT_DATE]: appointment?.appointment_date || '',
        [FormKeys.PRIORITY_1]: '',
        [FormKeys.PRIORITY_2]: '',
        [FormKeys.PRIORITY_3]: '',
        [FormKeys.PRIORITY_4]: '',
    });

    type PriorityKey = keyof Pick<FormValues, FormKeys.PRIORITY_1 | FormKeys.PRIORITY_2 | FormKeys.PRIORITY_3 | FormKeys.PRIORITY_4>;
    const priorities: PriorityKey[] = [
        FormKeys.PRIORITY_1,
        FormKeys.PRIORITY_2,
        FormKeys.PRIORITY_3,
        FormKeys.PRIORITY_4
    ]

    const [globalFilter, setGlobalFilter] = useState("");
    const [prioritizeJoints, setPrioritizeJoints] = useState(true);

    const [showPatientDropdown, setShowPatientDropdown] = useState(false);
    const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);

    const { allPatients } = usePatientData({ preventRefetch: false });

    const handleSearchChange = (value: string) => {
        setGlobalFilter(value);

        if (value.trim().length > 0 && allPatients) {
            const filtered = allPatients.filter(patient =>
                patient.first_name.toLowerCase().includes(value.toLowerCase()) ||
                patient.last_name.toLowerCase().includes(value.toLowerCase()) ||
                patient.external_id?.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredPatients(filtered);
            setShowPatientDropdown(true);
        } else {
            setFilteredPatients([]);
            setShowPatientDropdown(false);
        }
    };

    const allJointKeys = [
        FormKeys.RIGHT_KNEE,
        FormKeys.LEFT_KNEE,
        FormKeys.RIGHT_HIP,
        FormKeys.LEFT_HIP,
    ];

    const getSelectedJoints = () => allJointKeys.filter(key => formValues[key]);

    const getAvailableJointsForPriority = () => allJointKeys.filter(joint => formValues[joint] !== false);

    const createPriorityDropdownOptions = (availableJoints: typeof allJointKeys, priorityIndex: number) =>
        availableJoints.map((joint) => ({
            value: snakeToTitleCase(joint),
            handleClick: () => handlePrioritySelection(joint, priorityIndex),
        }));

    const getPriorityDisplayValue = (priorityIndex: number) => {
        const priorityKey = `PRIORITY_${priorityIndex + 1}` as keyof FormValues;
        const rawValue = formValues[priorityKey] as string;
        const isJointStillSelected = rawValue && allJointKeys.includes(rawValue as any) && formValues[rawValue as keyof FormValues];
        const display = snakeToTitleCase((rawValue && isJointStillSelected) ? rawValue : `Priority ${priorityIndex + 1}`);
        return display;
    }

    const handlePrioritySelection = (joint: string, priorityIndex: number) => {
        const priorityKey = FormKeys[`PRIORITY_${priorityIndex + 1}` as keyof typeof FormKeys] as PriorityKey;

        const existingIndex = priorities.findIndex((p) => formValues[p] === joint);

        if (existingIndex !== -1 && existingIndex !== priorityIndex) {
            const updates: Partial<Pick<FormValues, PriorityKey>> = {};
            updates[priorities[existingIndex]] = '' as FormValues[PriorityKey];
            updates[priorityKey] = joint as FormValues[PriorityKey];
            setFormValues(updates as Partial<FormValues>);
            return;
        }

        setFormValue(priorityKey as keyof FormValues, joint);
    }

    const clearJointFromPriorities = (jointKey: string) => {
        const compacted = priorities
            .map((p) => formValues[p])
            .filter((val): val is string => !!val && val !== jointKey);

        const updates: Partial<Pick<FormValues, PriorityKey>> = {};

        for (let i = 0; i < priorities.length; i++) {
            updates[priorities[i]] = (compacted[i] || '') as FormValues[PriorityKey];
        }

        setFormValues(updates as Partial<FormValues>);
    }

    const handleJointToggle = (jointKey: keyof FormValues) => {
        const isCurrentlySelected = formValues[jointKey];
        if (isCurrentlySelected) clearJointFromPriorities(jointKey as string);
        setFormValue(jointKey, !isCurrentlySelected);
    }

    const selectedJoints = getSelectedJoints();
    const availableJointsForPriority = getAvailableJointsForPriority();

    const getPriorityRankForJoint = (jointKey: string): number => {
        const idx = priorities.findIndex((p) => formValues[p] === jointKey);
        return idx === -1 ? 1 : idx + 1;
    }

    const createPatientHandler = async () => {
        try {
            const resp = await createPatient({
                org_id: org!.org_id,
                first_name: formValues[FormKeys.PATIENT_FIRST_NAME],
                last_name: formValues[FormKeys.PATIENT_LAST_NAME],
                external_id: formValues[FormKeys.PATIENT_ID_NUMBER],
                date_of_birth: formValues[FormKeys.PATIENT_DOB],
                provider: formValues[FormKeys.PROVIDER]
            });

            const newPatient = resp.data.patient;
            await createAppointment({
                patient_id: newPatient.patient_id,
                appointment_date: new Date(formValues[FormKeys.APPT_DATE]).toISOString().split('T')[0],
                org_id: user!.orgs[0].org_id,
                joints: {
                    ...(formValues[FormKeys.RIGHT_KNEE] ? { [FullJoints.RIGHT_KNEE]: getPriorityRankForJoint(FormKeys.RIGHT_KNEE) } : null),
                    ...(formValues[FormKeys.LEFT_KNEE] ? { [FullJoints.LEFT_KNEE]: getPriorityRankForJoint(FormKeys.LEFT_KNEE) } : null),
                    ...(formValues[FormKeys.RIGHT_HIP] ? { [FullJoints.RIGHT_HIP]: getPriorityRankForJoint(FormKeys.RIGHT_HIP) } : null),
                    ...(formValues[FormKeys.LEFT_HIP] ? { [FullJoints.LEFT_HIP]: getPriorityRankForJoint(FormKeys.LEFT_HIP) } : null),
                },
                isTriage: true
            });

            if (refetchTriageAppointmentStatuses) {
                await refetchTriageAppointmentStatuses();
            }

            onSuccess()

        } catch (e) {
            console.error(`Something went wrong: ${e}`);
        }
    }

    const updatePatientHandler = async () => {

        if (!patient || !appointment) {
            return
        }

        try {
            await updatePatient({
                org_id: org!.org_id,
                patient_id: patient.patient_id,
                first_name: formValues[FormKeys.PATIENT_FIRST_NAME],
                last_name: formValues[FormKeys.PATIENT_LAST_NAME],
                external_id: formValues[FormKeys.PATIENT_ID_NUMBER],
                date_of_birth: formValues[FormKeys.PATIENT_DOB],
                provider: formValues[FormKeys.PROVIDER]
            });

            await updateAppointment({
                appointmentDate: new Date(formValues[FormKeys.APPT_DATE]).toISOString().split('T')[0],
                appointmentId: appointment.appointment_id,
                newValues: {
                    joints: {
                        ...(formValues[FormKeys.RIGHT_KNEE] ? { [FullJoints.RIGHT_KNEE]: getPriorityRankForJoint(FormKeys.RIGHT_KNEE) } : null),
                        ...(formValues[FormKeys.LEFT_KNEE] ? { [FullJoints.LEFT_KNEE]: getPriorityRankForJoint(FormKeys.LEFT_KNEE) } : null),
                        ...(formValues[FormKeys.RIGHT_HIP] ? { [FullJoints.RIGHT_HIP]: getPriorityRankForJoint(FormKeys.RIGHT_HIP) } : null),
                        ...(formValues[FormKeys.LEFT_HIP] ? { [FullJoints.LEFT_HIP]: getPriorityRankForJoint(FormKeys.LEFT_HIP) } : null),
                    },
                }
            });

            onSuccess()
        }
        catch (e) {

            console.error(`Something went wrong: ${e}`);
        }
    }

    const handlePatientSelect = (selectedPatient: Patient) => {

        setFormValues({
            [FormKeys.PATIENT_FIRST_NAME]: selectedPatient.first_name,
            [FormKeys.PATIENT_LAST_NAME]: selectedPatient.last_name,
            [FormKeys.PATIENT_ID_NUMBER]: selectedPatient.external_id || '',
            [FormKeys.PATIENT_DOB]: selectedPatient.date_of_birth
                ? new Date(selectedPatient.date_of_birth).toISOString().split('T')[0]
                : ''
        });

        setGlobalFilter('');
        setShowPatientDropdown(false);
        setFilteredPatients([]);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (!target.closest('.search-container')) {
                setShowPatientDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <>
            <ModalTitle fontWeight={700} fontSize={"1.8rem"} >{isEdit ? 'Edit' : 'Enter'} Patient Information</ModalTitle>

            {(createPatientError || updatePatientError || updateAppointmentError || createAppointmentError) && <ErrorText>
                Something went wrong. Please try again.
            </ErrorText>}

            <CloseIcon
                icon={faX}
                color={colors.primary.main}
                cursor='pointer'
                onClick={closeModal}
            />

            <SearchContainer className="search-container">
                <MDInput
                    placeholder={"Search Existing Patients"}
                    value={globalFilter}
                    size="medium"
                    fullWidth
                    onChange={({ currentTarget }) => {
                        handleSearchChange(currentTarget.value);
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="medium" />
                            </InputAdornment>
                        ),
                    }}
                />

                {showPatientDropdown && filteredPatients.length > 0 && (
                    <PatientSearchDropdown>
                        {filteredPatients.map((patient) => (
                            <PatientSearchDropdownItem
                                key={patient.patient_id}
                                onMouseDown={() => handlePatientSelect(patient)}
                                onClick={() => handlePatientSelect(patient)}
                            >
                                <Typography variant="body2" fontWeight={600}>
                                    {patient.first_name} {patient.last_name}
                                </Typography>
                                {patient.external_id && (
                                    <Typography variant="caption" color="textSecondary">
                                        ID: {patient.external_id}
                                    </Typography>
                                )}
                            </PatientSearchDropdownItem>
                        ))}
                    </PatientSearchDropdown>
                )}
            </SearchContainer>
            <FormContainer>
                <MDInput
                    dark
                    label='First name'
                    name={FormKeys.PATIENT_FIRST_NAME}
                    value={formValues[FormKeys.PATIENT_FIRST_NAME]}
                    onChange={handleChange}
                    fullWidth
                    style={{ marginBottom: '0.5rem' }}
                />
                <MDInput
                    dark
                    label='Last Name'
                    name={FormKeys.PATIENT_LAST_NAME}
                    value={formValues[FormKeys.PATIENT_LAST_NAME]}
                    onChange={handleChange}
                    fullWidth
                    style={{ marginBottom: '0.5rem' }}
                />
                <MDInput
                    dark
                    fullWidth
                    label='ID'
                    name={FormKeys.PATIENT_ID_NUMBER}
                    type='text'
                    value={formValues[FormKeys.PATIENT_ID_NUMBER]}
                    onChange={handleChange}
                />
                <DatePickerWrapper>
                    <DatePicker
                        selected={formValues[FormKeys.PATIENT_DOB] ? new Date(formValues[FormKeys.PATIENT_DOB]) : null}
                        onChange={(date) => setFormValue(FormKeys.PATIENT_DOB, date ? date.toISOString().split('T')[0] : '')}
                        customInput={
                            <MDInput
                                dark
                                label="Date of Birth"
                                name={FormKeys.PATIENT_DOB}
                                fullWidth
                                error={
                                    formValues[FormKeys.PATIENT_DOB] === ''
                                        ? false
                                        : !validateDateFormat(formValues[FormKeys.PATIENT_DOB])
                                }
                            />
                        }
                        dateFormat="MM/dd/yyyy"
                        placeholderText="MM/DD/YYYY"
                        dropdownMode="select"
                        popperPlacement="bottom-start"
                        popperModifiers={[
                            {
                                name: 'preventOverflow',
                                enabled: false,
                            },
                            {
                                name: 'flip',
                                enabled: false,
                            },
                        ]}
                    />
                </DatePickerWrapper>
                <MDInput
                    style={{ marginBottom: '0.5rem' }}
                    dark
                    label='Provider'
                    name={FormKeys.PROVIDER}
                    value={formValues[FormKeys.PROVIDER]}
                    onChange={handleChange}
                    fullWidth
                />
                <DatePickerWrapper>
                    <DatePicker
                        selected={formValues[FormKeys.APPT_DATE] ? new Date(formValues[FormKeys.APPT_DATE]) : null}
                        onChange={(date) => setFormValue(FormKeys.APPT_DATE, date ? date.toISOString().split('T')[0] : '')}
                        customInput={
                            <MDInput
                                dark
                                label="Appt. Date"
                                name={FormKeys.APPT_DATE}
                                fullWidth
                                error={
                                    formValues[FormKeys.APPT_DATE] === ''
                                        ? false
                                        : !validateDateFormat(formValues[FormKeys.APPT_DATE])
                                }
                            />
                        }
                        dateFormat="MM/dd/yyyy"
                        placeholderText="MM/DD/YYYY"
                        dropdownMode="select"
                        popperPlacement="bottom-start"
                        popperModifiers={[
                            {
                                name: 'preventOverflow',
                                enabled: false,
                            },
                            {
                                name: 'flip',
                                enabled: false,
                            },
                        ]}
                    />
                </DatePickerWrapper>
            </FormContainer>
            <JointSelectionContainer>
                <JointSelectionBox gridColumn='1 / -1' display='flex' flexDirection='column' justifyContent='center' alignItems='center'>
                    <MDBox>
                        <JointSelectionTitle fontWeight={600} fontSize={'1rem'}> Select Joint with X-Rays </JointSelectionTitle>
                        <JointOptionsContainer display="grid" gridTemplateColumns="1fr 1fr" columnGap="3rem">
                            {allJointKeys.map(key => (
                                <JointLabel key={key}>
                                    <JointCheckbox
                                        type="checkbox"
                                        name={key}
                                        checked={!!formValues[key]}
                                        onChange={() => handleJointToggle(key)}
                                    />
                                    <JointText fontWeight={600} fontSize={'1rem'}> {snakeToTitleCase(key)}</JointText>
                                </JointLabel>
                            ))}
                        </JointOptionsContainer>
                    </MDBox>
                </JointSelectionBox>
                <MDBox>
                    <MDBox>
                        <PriorityContainer>
                            <PriorityTitle fontWeight={600} fontSize={'1rem'}> Prioritize Joints</PriorityTitle>
                            <PrioritySwitchContainer>
                                <Switch
                                    onClick={() => setPrioritizeJoints(!prioritizeJoints)}
                                    onLabel='Yes'
                                    offLabel='No'
                                    isOn={prioritizeJoints}
                                />
                            </PrioritySwitchContainer>
                        </PriorityContainer>
                    </MDBox>
                    {prioritizeJoints && (
                        <PriorityDropdownContainer>
                            {selectedJoints.map((jointKey, index) => (
                                <DropdownSelect
                                    label={`Priority ${index + 1}`}
                                    placeholder={`Priority ${index + 1}`}
                                    options={createPriorityDropdownOptions(availableJointsForPriority, index)}
                                    value={getPriorityDisplayValue(index)}
                                    style={{ marginBottom: '0.5rem' }}
                                />
                            ))}
                        </PriorityDropdownContainer>
                    )}
                </MDBox>
            </JointSelectionContainer>

            <MDButton
                color={MUIColors.SECONDARY}
                variant='contained'
                disabled={
                    createPatientLoading || updatePatientLoading || createAppointmentLoading || updateAppointmentLoading ||
                    !formValues[FormKeys.PATIENT_FIRST_NAME] ||
                    !formValues[FormKeys.PATIENT_LAST_NAME] ||
                    !formValues[FormKeys.PATIENT_ID_NUMBER] ||
                    !formValues[FormKeys.APPT_DATE] ||
                    (
                        formValues[FormKeys.PATIENT_DOB] &&
                        !validateDateFormat(formValues[FormKeys.PATIENT_DOB])
                    ) ||
                    (
                        formValues[FormKeys.APPT_DATE] &&
                        !validateDateFormat(formValues[FormKeys.APPT_DATE])
                    ) ||
                    (
                        !formValues[FormKeys.RIGHT_KNEE] &&
                        !formValues[FormKeys.LEFT_KNEE] &&
                        !formValues[FormKeys.RIGHT_HIP] &&
                        !formValues[FormKeys.LEFT_HIP]
                    )
                }
                onClick={async () => {
                    isEdit ? updatePatientHandler() : createPatientHandler()
                }}
            >
                {isEdit ? 'Save' : 'Create'}
            </MDButton>
        </>
    )
}

const ContentFallback: React.FC<AddPatientModalContentProps> = ({ closeModal, onSuccess, isEdit }) => {
    return (<>
        <ModalTitle fontWeight={700} fontSize={"1.8rem"}>Edit Patient Information</ModalTitle>

        <CloseIcon
            icon={faX}
            color={colors.primary.main}
            cursor='pointer'
            onClick={closeModal}
        />

        <Spinner />
    </>)
}

interface AddPatientModalLoaderProps {
    closeModal(): void;
    onSuccess(): void;
    isEdit?: boolean;
    patient?: Patient | null;
    appointmentId?: string;
    appointmentDate?: string;
    refetchTriageAppointmentStatuses?: () => Promise<any>;
}

const Loader: React.FC<AddPatientModalLoaderProps> = ({ closeModal, onSuccess, isEdit, patient, appointmentId, appointmentDate, refetchTriageAppointmentStatuses }) => {
    const {
        appointment,
        appointmentLoading,
    } = useAppointmentData({ appointmentId: appointmentId, selectedDateISOString: appointmentDate, enabled: !isEdit });

    const { breakpointBreached } = useBreakpoints({ breakpoint: Breakpoints.SMALL });
    return <Modal
        {...containerStyles({
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        })}
        open={true}
        onClose={closeModal}
    >
        <ModalContainer
            shadow={Shadows.SMALL}
            borderRadius={borders.borderRadius.lg}
            breakpointBreached={breakpointBreached}
        >
            {
                appointmentLoading
                    ? <ContentFallback closeModal={closeModal} onSuccess={onSuccess} isEdit={isEdit} />
                    : <Content closeModal={closeModal} onSuccess={onSuccess} isEdit={isEdit} patient={patient} appointment={appointment} refetchTriageAppointmentStatuses={refetchTriageAppointmentStatuses} />
            }
        </ModalContainer>
    </Modal >
}

export default Loader
